import React, { useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { speakerData } from "../../data/speaker-list";

const ProductCard = ({ product, category }) => {
  const [expanded, setExpanded] = useState(false);

  // Use a placeholder image if the product image is not available
  const imageUrl = product.productImage || "/api/placeholder/200/200";

  // Determine which properties to show based on category
  const renderProperties = () => {
    const commonProps = (
      <>
        <div className="text-sm font-medium text-gray-600">
          Size:{" "}
          <span className="text-gray-900 ">{product.size}</span>
        </div>
        {product.sizeInch && (
          <div className="text-sm font-medium text-gray-600">
            Size (inch):{" "}
            <span className="text-gray-900">
              {product.sizeInch}
            </span>
          </div>
        )}
        <div className="text-sm font-medium text-gray-600">
          Impedance:{" "}
          <span className="text-gray-900">
            {product.impedance}
          </span>
        </div>
        {product.nominalPower && (
          <div className="text-sm font-medium text-gray-600">
            Nominal Power:{" "}
            <span className="text-gray-900">
              {product.nominalPower}
            </span>
          </div>
        )}
        {product.maximumPower && (
          <div className="text-sm font-medium text-gray-600">
            Maximum Power:{" "}
            <span className="text-gray-900">
              {product.maximumPower}
            </span>
          </div>
        )}
      </>
    );

    if (category === "speakerSystems") {
      return (
        <>
          {commonProps}
          <div className="text-sm font-medium text-gray-600">
            Drive Unit:{" "}
            <span className="text-gray-900">
              {product.driveUnit}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-600">
            Dimensions:{" "}
            <span className="text-gray-900">
              {product.dimensions}
            </span>
          </div>
        </>
      );
    }

    return commonProps;
  };

  // Render additional details when expanded
  const renderExpandedDetails = () => {
    if (category === "speakerSystems") {
      return (
        <>
          <div className="text-sm font-medium text-gray-600">
            Frequency Range:{" "}
            <span className="text-gray-900">
              {product.frequencyRange}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-600">
            Sound Pressure Level:{" "}
            <span className="text-gray-900">
              {product.soundPressureLevel}
            </span>
          </div>
          <div className="text-sm font-medium text-gray-600">
            Total Mass:{" "}
            <span className="text-gray-900">
              {product.totalMass}
            </span>
          </div>

          <div className="mt-2">
            <h4 className="text-sm font-bold text-gray-700">
              Externals
            </h4>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {Object.entries(product.externals).map(([key, value]) => (
                <div
                  key={key}
                  className="text-xs text-gray-600"
                >
                  <span className="capitalize">{key}:</span> {value}
                </div>
              ))}
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {product.resonantFrequency && (
          <div className="text-sm font-medium text-gray-600">
            Resonant Frequency:{" "}
            <span className="text-gray-900">
              {product.resonantFrequency}
            </span>
          </div>
        )}
        {product.frequencyRange && (
          <div className="text-sm font-medium text-gray-600">
            Frequency Range:{" "}
            <span className="text-gray-900">
              {product.frequencyRange}
            </span>
          </div>
        )}
        {product.soundPressureLevel && (
          <div className="text-sm font-medium text-gray-600">
            Sound Pressure Level:{" "}
            <span className="text-gray-900">
              {product.soundPressureLevel}
            </span>
          </div>
        )}
        {product.voiceCoilDiameter && (
          <div className="text-sm font-medium text-gray-600">
            Voice Coil Diameter:{" "}
            <span className="text-gray-900">
              {product.voiceCoilDiameter}
            </span>
          </div>
        )}
        {product.magnetMass && (
          <div className="text-sm font-medium text-gray-600 ">
            Magnet Mass:{" "}
            <span className="text-gray-900 ">
              {product.magnetMass}
            </span>
          </div>
        )}
        {product.totalMass && (
          <div className="text-sm font-medium text-gray-600 ">
            Total Mass:{" "}
            <span className="text-gray-900 ">
              {product.totalMass}
            </span>
          </div>
        )}

        {product.externals && (
          <div className="mt-2">
            <h4 className="text-sm font-bold text-gray-700 ">
              Externals
            </h4>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {Object.entries(product.externals).map(([key, value]) => (
                <div
                  key={key}
                  className="text-xs text-gray-600 "
                >
                  <span className="capitalize">{key}:</span> {value}
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="bg-white  rounded-lg shadow-md overflow-hidden transform transition-all hover:shadow-lg border border-gray-200">
      <div className="relative pb-3/4">
        <img
          src={imageUrl}
          alt={`${product.model} speaker`}
          className="h-48 w-full object-contain bg-gray-100"
        />
      </div>
      <div className="p-4">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-bold text-gray-900 ">
            {product.model}
          </h3>
          <div className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">
            {category === "fullRangeGeneral"
              ? "Full Range"
              : category === "fullRangeOval"
              ? "Full Range Oval"
              : category === "tweeters"
              ? "Tweeter"
              : category === "woofers"
              ? "Woofer"
              : category === "microSpeakers"
              ? "Micro Speaker"
              : "Speaker System"}
          </div>
        </div>

        <div className="mt-3 space-y-1">{renderProperties()}</div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
            {renderExpandedDetails()}
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          {expanded ? (
            <>
              <ChevronUp size={16} className="mr-1" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown size={16} className="mr-1" />
              Show More
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const SpeakerCatalog = () => {
  const [activeCategory, setActiveCategory] = useState("fullRangeGeneral");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    impedance: "",
    size: "",
    power: "",
  });

  // Reset filters when category changes
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    // Reset all filters and search term
    setSearchTerm("");
    setFilterOptions({
      impedance: "",
      size: "",
      power: "",
    });
  };

  // Generate unique options for filter dropdowns based on current category
  const getUniqueOptions = (field) => {
    // Only use products from the current category for filters
    const products = speakerData[activeCategory] || [];
    
    const options = new Set();
    
    if (field === 'impedance') {
      products.forEach(product => {
        if (product.impedance) options.add(product.impedance);
      });
    } else if (field === 'size') {
      products.forEach(product => {
        if (product.size) options.add(product.size);
      });
    } else if (field === 'power') {
      products.forEach(product => {
        if (product.nominalPower) options.add(product.nominalPower);
      });
    }
    
    return Array.from(options).sort();
  };

  // Filter the products based on search and filter options
  const getFilteredProducts = () => {
    let products = speakerData[activeCategory] || [];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      products = products.filter(
        (product) =>
          product.model.toLowerCase().includes(search) ||
          (product.size && product.size.toLowerCase().includes(search))
      );
    }

    // Apply dropdown filters
    if (filterOptions.impedance) {
      products = products.filter(
        (product) => product.impedance === filterOptions.impedance
      );
    }

    if (filterOptions.size) {
      products = products.filter(
        (product) => product.size === filterOptions.size
      );
    }

    if (filterOptions.power) {
      products = products.filter(
        (product) => product.nominalPower === filterOptions.power
      );
    }

    return products;
  };

  const filteredProducts = getFilteredProducts();

  const categories = [
    { id: "fullRangeGeneral", name: "Full Range General" },
    { id: "fullRangeOval", name: "Full Range Oval" },
    { id: "tweeters", name: "Tweeters" },
    { id: "woofers", name: "Woofers" },
    { id: "microSpeakers", name: "Micro Speakers" },
    { id: "speakerSystems", name: "Speaker Systems" },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="w-full my-6 text-sectionTitle2 md:text-sectionTitle1 text-center">
        Our Products
      </h1>
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 my-6">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeCategory === category.id
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-800  hover:bg-gray-300 "
            }`}
            onClick={() => handleCategoryChange(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="col-span-1 md:col-span-1">
          <input
            type="text"
            placeholder="Search by model or size..."
            className="w-full px-4 py-2 rounded-lg border border-gray-300  bg-white  text-gray-900  focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <select
            className="w-full px-4 py-2 rounded-lg border border-gray-300  bg-white  text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filterOptions.impedance}
            onChange={(e) =>
              setFilterOptions({ ...filterOptions, impedance: e.target.value })
            }
          >
            <option value="">Filter by Impedance</option>
            {getUniqueOptions("impedance").map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            className="w-full px-4 py-2 rounded-lg border border-gray-300  bg-white  text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filterOptions.size}
            onChange={(e) =>
              setFilterOptions({ ...filterOptions, size: e.target.value })
            }
          >
            <option value="">Filter by Size</option>
            {getUniqueOptions("size").map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white  text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filterOptions.power}
            onChange={(e) =>
              setFilterOptions({ ...filterOptions, power: e.target.value })
            }
          >
            <option value="">Filter by Power</option>
            {getUniqueOptions("power").map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product, index) => (
            <ProductCard
              key={`${product.model}-${index}`}
              product={product}
              category={activeCategory}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <Info size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            No products found
          </h2>
          <p className="text-gray-600 ">
            Try adjusting your search or filter criteria to find products.
          </p>
        </div>
      )}

      {/* Product Count */}
      <div className="mt-6 text-gray-600">
        Showing {filteredProducts.length} of{" "}
        {speakerData[activeCategory]?.length || 0} products
      </div>
    </div>
  );
};

export default SpeakerCatalog;